import { atom, computed } from 'nanostores';
import produce, {
  enableMapSet,
  enablePatches,
  applyPatches,
  produceWithPatches,
} from 'immer';

import { csrfFetch, nextFrame } from '../../../lib/helpers.js';
import { addComputedPropsToChat } from './compute_state_for_current_chat.js';

// ImmerJS configuration
enableMapSet();
enablePatches();

/*
  store = {
    chats: {
      1: {id: 1, members: [1, 2, 3], messages: {1: {...}, 2: {...}}}
      2: {id: 2, members: [1, 4, 5], messages: {3: {...}, 4: {...}}}
    }
  }
*/

export const store = atom({
  chats: new Map(),
});

window.store = store;

export const initialChatState = {
  isMuted: false,
  isBlocked: false,
  createdAt: new Date().toJSON(),
  kind: 'direct',
  messages: new Map(),
  adminIds: [],
  // We need to set it to undefined to ease the testing
  unreadMessagesCount: undefined,
};

function prepareMessage(message) {
  return message;
}

function appendReadIdsInPatch(patch) {
  patch.forEach(p => {
    const attribute = p.path.at(-2);
    if (p.op === 'add' && attribute === 'readIds') {
      p.path = [...p.path.slice(0, -1), '-'];
    }
  });
}

// upsertChat
export function initialize({ chatId, ...attributes }) {
  const nextState = produce(store.value, draft => {
    const chat = draft.chats.get(chatId);
    draft.chats.set(chatId, {
      ...initialChatState,
      ...chat,
      chatId,
      ...attributes,
    });
  });

  store.set(nextState);
}

export function getChat(chatId) {
  return store.value.chats.get(String(chatId));
}

export function upsertAll(obj, newItems, key = 'uuid') {
  if (!newItems?.length) return obj;

  return produce(obj, draft => {
    newItems.forEach(item => {
      const preparedItem = prepareMessage(item);

      const message = draft.get(item[key]);

      if (message) {
        Object.assign(message, preparedItem);
      } else {
        draft.set(item[key], preparedItem);
      }
    });
  });
}

const upsertMessagesProducer = produce(
  (draft, { chatId, messages, unreadMessagesCount, ...attributes }) => {
    const chats = draft.chats;
    chats.has(chatId) || chats.set(chatId, { ...initialChatState, chatId });

    const chat = chats.get(chatId);
    chat.messages ||= new Map();
    chat.messages = upsertAll(chat.messages, messages);

    // Once we start tracking unreadMessagesCount in the frontend,
    // we don't want to override it with the value from the backend
    chat.unreadMessagesCount ??= unreadMessagesCount;

    // Update chat attributes (messages, unreadMessagesCount)
    attributes && Object.assign(chats.get(chatId), attributes);
  }
);

export function upsertMessages({ chatId, messages, ...attributes }) {
  const nextState = upsertMessagesProducer(store.value, {
    chatId: String(chatId),
    messages,
    ...attributes,
  });

  store.set(nextState);

  return nextState;
}

const upsertChatsProducer = produce((draft, { chats }) => {
  let nextState = draft;

  for (const chat of chats) {
    nextState = upsertMessagesProducer(nextState, {
      ...chat,
      chatId: String(chat.chatId),
    });
  }
  return nextState;
});

export function upsertChats({ chats }) {
  let nextState = upsertChatsProducer(store.value, { chats });
  store.set(nextState);
}

export function removeChat({ chatId }) {
  const nextState = produce(store.value, draft => {
    draft.chats.delete(chatId);
  });

  store.set(nextState);
}

export function getMessage({ chatId, uuid }) {
  return store.value.chats.get(chatId).messages.get(uuid);
}

export function insertMessage({ chatId, message }) {
  const { uuid } = message;
  const [nextState, patches] = produceWithPatches(store.value, draft => {
    const messages = draft.chats.get(chatId).messages;
    messages.set(message.uuid, prepareMessage(message));
  });

  if (patches.length) {
    store.set(postProcessPatchesForMessages(nextState, patches));
  }

  return [patches, getMessage({ chatId, uuid })];
}

function postProcessPatchesForMessages(prevState, patches) {
  const currentUserId = currentUserStore.value?.id;

  return produce(prevState, draft => {
    // Skip if there are no chats
    if (!draft.chats || draft.chats.size === 0) return;

    patches.forEach(patch => {
      const { op, path, value } = patch;
      const [_chats, chatId, _messages, messageId, attribute] = path;

      const chat = draft.chats.get(chatId);
      const message = chat.messages.get(messageId);

      message._counted ||= {
        up: false,
        down: false,
      };

      const isReceived =
        patch.op == 'add' &&
        patch.path.length == 4 &&
        value.userId !== currentUserId;

      const isMessageReadByCurrentUser =
        op === 'add' && attribute === 'readIds' && value === currentUserId;

      if (isReceived && !message._counted.up) {
        chat.unreadMessagesCount++;
        message._counted.up = true;
      }

      if (
        isMessageReadByCurrentUser &&
        !message._counted.down &&
        chat.unreadMessagesCount > 0
      ) {
        chat.unreadMessagesCount--;
        message._counted.down = true;
      }
    });
  });
}

export function updateMessage({
  chatId,
  uuid,
  // seenAt,
  readByUserId,
  ...params
}) {
  const [nextState, patches] = produceWithPatches(store.value, draft => {
    const chat = draft.chats.get(chatId);
    const item = chat.messages.get(uuid);
    Object.assign(item, prepareMessage(params));
    // item.seenAt ??= seenAt; // TODO: resolve later

    if (readByUserId && !item.readIds.includes(readByUserId)) {
      item.readIds.push(readByUserId);
    }
  });

  appendReadIdsInPatch(patches);

  if (patches.length) {
    store.set(postProcessPatchesForMessages(nextState, patches));
  }

  return [patches, getMessage({ chatId, uuid })];
}

// This will only update chat attributes, not messages
export function updateChat({ chatId, ...params }) {
  const [nextState, patches] = produceWithPatches(store.value, draft => {
    Object.assign(draft.chats.get(chatId), params);
  });

  if (patches.length) store.set(nextState);

  return [patches, getChat(chatId)];
}

export function removeMessage({ chatId, uuid }) {
  const item = store.value.chats.get(chatId).messages.get(uuid);

  const [nextState, patches] = produceWithPatches(store.value, draft => {
    draft.chats.get(chatId).messages.delete(uuid);
  });

  if (patches.length) store.set(nextState);

  return [patches, item];
}

export function markAllAsReadByUser({ chatId, userId, unreadMessagesIds }) {
  const [nextState, patches] = produceWithPatches(store.value, draft => {
    unreadMessagesIds.forEach(uuid => {
      draft.chats.get(chatId).messages.get(uuid).readIds.push(userId);
    });
  });

  appendReadIdsInPatch(patches);
  if (patches.length)
    store.set(postProcessPatchesForMessages(nextState, patches));

  // We return array of patches so it matches the return value of other functions eg. updateMessage
  return [patches];
}

export async function markChatAsRead({ chatId, url }) {
  const fetchUrl = new URL(url, window.location.origin);

  await csrfFetch(fetchUrl, { method: 'POST' });

  store.set(
    produce(store.value, draft => {
      // TBD: should mark all messages as read too?
      const chat = draft.chats.get(chatId);
      chat.unreadMessagesCount = 0;
    })
  );
}

export async function markAllChatsFromDBAsRead(url) {
  try {
    const fetchUrl = new URL(url, window.location.origin);

    await csrfFetch(fetchUrl, { method: 'POST' });

    for (let chat of computedChatsStore.value.chats.values()) {
      // will decrement unreadMessagesCount
      markAllAsReadByUser({
        chatId: chat.chatId,
        userId: currentUserStore.value.id,
        unreadMessagesIds: chat.unreadMessages.map(m => m.uuid),
      });

      // decrement count for non-loaded messages
      updateChat({ chatId: chat.chatId, unreadMessagesCount: 0 });
    }
  } catch (e) {
    console.error('Failed to mark all as read', e.message);
  }
}

export function applyPatchesToStore(patches) {
  const nextState = applyPatches(store.value, patches);
  store.set(postProcessPatchesForMessages(nextState, patches));
}

export async function loadMessages({
  chatId,
  chatUrl,
  query,
  showLoader = true,
  perPage,
}) {
  if (!perPage) throw new Error('perPage is not defined');

  store.set(
    produce(store.value, draft => {
      draft.chats.get(chatId).loading = showLoader;
    })
  );

  var url = new URL(chatUrl, window.location.origin);
  url.search = new URLSearchParams({ ...query, perPage });
  try {
    const response = await csrfFetch(url, {
      headers: {
        accept: 'application/json',
      },
    }).then(r => r.json());
    const { messages } = response;
    // Clear error flags
    messages.forEach(message => {
      message.unsent = null;
      message.errors = null;
    });
    return upsertMessages({
      chatId,
      messages,
      loading: false,
      noMoreMessages: messages.length < perPage,
    });
  } catch (e) {
    updateChat({ chatId, loading: false, error: e.message });
    throw new Error(
      `Couldn't load messages for chat id: ${chatId}. ${e.message}`
    );
  }
}

export async function loadMessage({ chatUrl, chatId, messageId }) {
  var url = `${chatUrl}/${messageId}`;

  try {
    const message = await csrfFetch(url, {
      headers: {
        accept: 'application/json',
      },
    }).then(r => r.json());
    return upsertMessages({
      chatId,
      messages: [message],
    });
  } catch (e) {
    throw new Error(
      `Couldn't load message id: ${messageId} for chat id: ${chatId}. ${e.message}`
    );
  }
}

export async function loadChat(url, callback) {
  try {
    let fetchUrl = new URL(url, window.location.origin);

    const response = await csrfFetch(fetchUrl, {
      headers: {
        accept: 'application/json',
      },
    });

    const json = await response.json();
    const chat = callback ? callback(json) : json;

    upsertMessages({
      ...chat,
      loaded: true,
      chatId: String(chat.chatId),
    });
  } catch (e) {
    console.error(`Couldn't load a chat: ${e.message}`);
  }
}

export async function loadChats({ url = '/simulate/chats', query }) {
  // Fetch and upsert
  try {
    let fetchUrl = new URL(url, window.location.origin);

    // Append each query param
    for (const [key, value] of Object.entries(query)) {
      fetchUrl.searchParams.append(key, value);
    }

    const response = await csrfFetch(fetchUrl, {
      headers: {
        accept: 'application/json',
      },
    });

    const responseJSON = await response.json();
    const { chats } = responseJSON;

    // Mark chats as loaded
    chats.map(c => (c.loaded = true));

    let nextState = upsertChatsProducer(store.value, { chats });

    store.set(nextState);

    // Wait for next frame so that the UI can update
    await nextFrame();

    // console.log('LOAD CHATS', responseJSON);

    return responseJSON;
  } catch (e) {
    // Handle errors

    store.set(
      produce(store.value, draft => {
        draft.error = e.message;
      })
    );

    throw new Error("Chats couldn't be loaded: " + e.message);
  }
}

const CHAT_KINDS = ['direct', 'group', 'multi'];

export function setActive(chatId) {
  store.set(
    produce(store.value, draft => {
      draft.activeChatId = chatId && String(chatId);
    })
  );
}

export const currentUserStore = atom();

// Set current user from window.App.config.user so it works automatically when integrated on platform
if (window?.App?.config?.user) {
  currentUserStore.set(window.App.config.user);
}

let lastStoreValue = {
  chats: new Map(),
};
export const computedChatsStore = computed(
  [store, currentUserStore],
  (storeValue, currentUser) => {
    // Optimization: If chats haven't changed, return the same object
    if (lastStoreValue.chats === storeValue.chats) {
      return computedChatsStore.value;
    }

    // console.count('COMPUTED CHATS STORE');

    const chats = new Map();
    for (const plainChat of storeValue.chats.values()) {
      const { chatId } = plainChat;
      let chat;
      // console.log(lastStoreValue.chats.get(chatId) === plainChat);
      chat = computedChatsStore.value?.chats.get(chatId);

      if (lastStoreValue.chats.get(chatId) !== plainChat) {
        // console.count('  -- addComputedPropsToChat');
        chat = addComputedPropsToChat(plainChat, currentUser);
      }

      chats.set(chatId, chat);
    }

    // This way we only have one source of truth for unread counters
    const unreadCounters = {
      all: 0,
      muted: 0,
      direct: 0,
      group: 0,
      multi: 0,
    };

    const counters = {
      all: 0,
      muted: 0,
      direct: 0,
      group: 0,
      multi: 0,
    };

    for (let chat of chats.values()) {
      if (chat.unreadMessagesCount) {
        unreadCounters.all++;
        if (chat.isMuted) {
          unreadCounters.muted++;
        } else {
          CHAT_KINDS.forEach(kind => {
            if (chat.kind === kind) unreadCounters[kind]++;
          });
        }
      }

      counters.all++;

      if (chat.isMuted) {
        counters.muted++;
      } else {
        CHAT_KINDS.forEach(kind => {
          if (chat.kind === kind) counters[kind]++;
        });
      }

      chat.lastUpdatedAt = chat.lastSentMessage?.createdAt || chat.createdAt;
    }

    counters.allWithoutMuted = counters.all - counters.muted;
    unreadCounters.allWithoutMuted = unreadCounters.all - unreadCounters.muted;

    const sortedChats = [...chats.values()].sort((c1, c2) => {
      return c1.lastUpdatedAt > c2.lastUpdatedAt ? -1 : 1;
    });

    const firstChat = sortedChats.at(0);
    const lastChat = sortedChats.at(-1);

    const hasUnreadChats = unreadCounters.all > 0;
    const hasUnreadMutedChats = unreadCounters.muted > 0;

    const hasMutedChats = counters.muted > 0;

    const lastSentMessage = firstChat?.lastSentMessage;

    lastStoreValue = storeValue;

    return {
      ...storeValue,
      currentUser,
      hasUnreadChats,
      hasUnreadMutedChats,
      hasMutedChats,
      unreadCounters,
      counters,
      firstChat,
      lastChat,
      sortedChats,
      lastSentMessage,
      chats,
    };
  }
);

window.computedChatsStore = computedChatsStore;
